/**
 * Cube query builders for v1.1 dashboard.
 *
 * Each function returns a Promise of the parsed rows; failures surface as
 * CubeAuthError | CubeNetworkError | CubeQueryError per cube.ts.
 *
 * All queries scope by `period` (VersionDate) and optionally by Principal /
 * ProductType / Brand / SKU through the dim_product join.
 */

import { cubeLoad, type CubeFilter, type CubeQuery } from './cube.js';
import { store } from './stores.svelte.js';
import { IS_OFFLINE } from './data-mode.js';
import * as offline from './offline-adapter.js';

type Row = Record<string, unknown>;

const REG  = 'view_regression_v1_1';
const CITY = 'view_city_metrics';
const PROD = 'dim_product';

function periodFilter(period: string): CubeQuery['timeDimensions'] {
  return [{ dimension: `${REG}.version_date`, dateRange: [period, period] }];
}

function productFilters(): CubeFilter[] {
  const f: CubeFilter[] = [];
  if (store.principal)        f.push({ member: `${PROD}.principal`,    operator: 'equals', values: [store.principal] });
  if (store.productType)      f.push({ member: `${PROD}.product_type`, operator: 'equals', values: [store.productType] });
  if (store.brand)            f.push({ member: `${PROD}.brand`,        operator: 'equals', values: [store.brand] });
  if (store.sku_id !== null)  f.push({ member: `${PROD}.sku_id`,       operator: 'equals', values: [String(store.sku_id)] });
  return f;
}

/* ── 1. Bootstrap: list of available periods ────────────────────────────── */
export async function fetchPeriods(): Promise<string[]> {
  if (IS_OFFLINE) return offline.offline_fetchPeriods();
  const rows = await cubeLoad<Row>({
    dimensions: [`${REG}.version_date`],
    order:      { [`${REG}.version_date`]: 'desc' },
    limit:      24,
  });
  return rows.map((r) => String(r[`${REG}.version_date`]).slice(0, 10));
}

/* ── 2. Filter chain dropdowns ──────────────────────────────────────────── */
export interface FilterOption { value: string; count: number }

export async function fetchPrincipals(): Promise<FilterOption[]> {
  if (IS_OFFLINE) return offline.offline_fetchPrincipals();
  const rows = await cubeLoad<Row>({
    dimensions: [`${PROD}.principal`],
    measures:   [`${PROD}.count`],
    order:      { [`${PROD}.count`]: 'desc' },
    limit:      500,
  });
  return rows
    .filter((r) => r[`${PROD}.principal`])
    .map((r) => ({ value: String(r[`${PROD}.principal`]), count: Number(r[`${PROD}.count`] ?? 0) }));
}

export async function fetchProductTypes(principal: string): Promise<FilterOption[]> {
  if (IS_OFFLINE) return offline.offline_fetchProductTypes(principal);
  if (!principal) return [];
  const rows = await cubeLoad<Row>({
    dimensions: [`${PROD}.product_type`],
    measures:   [`${PROD}.count`],
    filters:    [{ member: `${PROD}.principal`, operator: 'equals', values: [principal] }],
    order:      { [`${PROD}.count`]: 'desc' },
    limit:      200,
  });
  return rows
    .filter((r) => r[`${PROD}.product_type`])
    .map((r) => ({ value: String(r[`${PROD}.product_type`]), count: Number(r[`${PROD}.count`] ?? 0) }));
}

export async function fetchBrands(principal: string, productType: string): Promise<FilterOption[]> {
  if (IS_OFFLINE) return offline.offline_fetchBrands(principal, productType);
  if (!principal) return [];
  const filters: CubeFilter[] = [{ member: `${PROD}.principal`, operator: 'equals', values: [principal] }];
  if (productType) filters.push({ member: `${PROD}.product_type`, operator: 'equals', values: [productType] });
  const rows = await cubeLoad<Row>({
    dimensions: [`${PROD}.brand`],
    measures:   [`${PROD}.count`],
    filters,
    order:      { [`${PROD}.count`]: 'desc' },
    limit:      500,
  });
  return rows
    .filter((r) => r[`${PROD}.brand`])
    .map((r) => ({ value: String(r[`${PROD}.brand`]), count: Number(r[`${PROD}.count`] ?? 0) }));
}

export async function fetchSkus(brand: string): Promise<{ sku_id: number; description: string }[]> {
  if (IS_OFFLINE) return offline.offline_fetchSkus(brand);
  if (!brand) return [];
  const rows = await cubeLoad<Row>({
    dimensions: [`${PROD}.sku_id`, `${PROD}.description`],
    filters:    [{ member: `${PROD}.brand`, operator: 'equals', values: [brand] }],
    order:      { [`${PROD}.sku_id`]: 'asc' },
    limit:      2000,
  });
  return rows.map((r) => ({
    sku_id:      Number(r[`${PROD}.sku_id`]),
    description: String(r[`${PROD}.description`] ?? ''),
  }));
}

/* ── 3. Aggregate KPIs for current filter + period ──────────────────────── */
export interface KpiTotals {
  my_gmv:  number;
  cat_gmv: number;
  sop:     number;
  share:   number;
  cities:  number;
}

export async function fetchKpiTotals(period: string): Promise<KpiTotals> {
  if (IS_OFFLINE) return offline.offline_fetchKpiTotals();
  const rows = await cubeLoad<Row>({
    measures: [
      `${REG}.my_gmv_8w`,
      `${REG}.cat_gmv_8w`,
      `${REG}.size_of_prize`,
      `${REG}.my_share`,
      `${REG}.count`,
    ],
    filters:        productFilters(),
    timeDimensions: periodFilter(period),
  });
  const a = rows[0] ?? {};
  return {
    my_gmv:  Number(a[`${REG}.my_gmv_8w`]    ?? 0),
    cat_gmv: Number(a[`${REG}.cat_gmv_8w`]   ?? 0),
    sop:     Number(a[`${REG}.size_of_prize`] ?? 0),
    share:   Number(a[`${REG}.my_share`]     ?? 0),
    cities:  Number(a[`${REG}.count`]        ?? 0),
  };
}

/* ── 4. Per-city rows for map choropleth + rankings ─────────────────────── */
export interface CityRow {
  city_id:   number;
  psgc:      string;
  city:      string;
  province:  string;
  region:    string;
  brand:     string;
  sku_id:    number | null;
  sku:       string;
  gpiv:      number;
  gpiv_tier: string;
  my_gmv:    number;
  cat_gmv:   number;
  sop:       number;
  share:     number;
  mp:        number;
  consumer_profile: string;
}

export async function fetchCityRows(period: string, limit = 2000): Promise<CityRow[]> {
  if (IS_OFFLINE) return offline.offline_fetchCityRows();
  const dims = [
    `${REG}.city_megastore_id`,
    `${CITY}.psgc_code`,
    `${CITY}.city`,
    `${CITY}.province`,
    `${CITY}.region`,
    `${CITY}.mp_score`,
    `${CITY}.consumer_profile_primary`,
    `${REG}.gpiv_tier`,
  ];
  const measures = [
    `${REG}.my_gmv_8w`,
    `${REG}.cat_gmv_8w`,
    `${REG}.size_of_prize`,
    `${REG}.my_share`,
    `${REG}.my_slope`,
  ];
  // When user has selected SKU/brand we also expose them for the table
  const showSku   = store.sku_id !== null;
  const showBrand = !!store.brand || showSku;
  if (showBrand) dims.push(`${PROD}.brand`);
  if (showSku)   dims.push(`${PROD}.sku_id`, `${PROD}.description`);

  const rows = await cubeLoad<Row>({
    dimensions:     dims,
    measures,
    filters:        productFilters(),
    timeDimensions: periodFilter(period),
    order:          { [`${REG}.size_of_prize`]: 'desc' },
    limit,
  });

  return rows.map((r) => ({
    city_id:   Number(r[`${REG}.city_megastore_id`]),
    psgc:      String(r[`${CITY}.psgc_code`] ?? ''),
    city:      String(r[`${CITY}.city`] ?? '—'),
    province:  String(r[`${CITY}.province`] ?? '—'),
    region:    String(r[`${CITY}.region`] ?? '—'),
    brand:     showBrand ? String(r[`${PROD}.brand`] ?? '') : '',
    sku_id:    showSku   ? Number(r[`${PROD}.sku_id`]) : null,
    sku:       showSku   ? String(r[`${PROD}.description`] ?? '') : '',
    gpiv:      Number(r[`${REG}.my_slope`] ?? 0),
    gpiv_tier: String(r[`${REG}.gpiv_tier`] ?? 'flat'),
    my_gmv:    Number(r[`${REG}.my_gmv_8w`]    ?? 0),
    cat_gmv:   Number(r[`${REG}.cat_gmv_8w`]   ?? 0),
    sop:       Number(r[`${REG}.size_of_prize`] ?? 0),
    share:     Number(r[`${REG}.my_share`]     ?? 0),
    mp:        Number(r[`${CITY}.mp_score`]    ?? 0),
    consumer_profile: String(r[`${CITY}.consumer_profile_primary`] ?? 'Mainstream'),
  }));
}

/* ── 5. Province-level rollup for map popup ─────────────────────────────── */
export interface ProvinceRow {
  province: string;
  cities:   number;
  my_gmv:   number;
  sop:      number;
}

export async function fetchProvinces(period: string): Promise<ProvinceRow[]> {
  if (IS_OFFLINE) return offline.offline_fetchProvinces();
  const rows = await cubeLoad<Row>({
    dimensions: [`${CITY}.province`],
    measures:   [`${REG}.my_gmv_8w`, `${REG}.size_of_prize`, `${REG}.count`],
    filters:        productFilters(),
    timeDimensions: periodFilter(period),
    order:          { [`${REG}.size_of_prize`]: 'desc' },
    limit:          200,
  });
  return rows.map((r) => ({
    province: String(r[`${CITY}.province`] ?? '—'),
    cities:   Number(r[`${REG}.count`] ?? 0),
    my_gmv:   Number(r[`${REG}.my_gmv_8w`] ?? 0),
    sop:      Number(r[`${REG}.size_of_prize`] ?? 0),
  }));
}

/* ── 6. Diagnostic: per-SKU breakdown for one city ──────────────────────── */
export interface SkuInCity {
  sku_id:    number;
  sku:       string;
  brand:     string;
  gpiv:      number;
  gpiv_tier: string;
  my_gmv:    number;
  cat_gmv:   number;
  sop:       number;
  share:     number;
}

/* ── 7. Diagnostic: province-level rollup (top SKUs + top cities) ───────── */
export interface SkuInScope extends SkuInCity { /* same shape */ }
export interface CityInProvince {
  city_id: number;
  city:    string;
  mp:      number;
  my_gmv:  number;
  cat_gmv: number;
  sop:     number;
  share:   number;
}
export interface ProvinceDiagnostic {
  totals: { my_gmv: number; cat_gmv: number; sop: number; share: number; cities: number };
  topCities: CityInProvince[];
  topSkus:   SkuInScope[];
}

export async function fetchProvinceDiagnostic(period: string, province: string): Promise<ProvinceDiagnostic> {
  const provinceFilter = { member: `${CITY}.province`, operator: 'equals' as const, values: [province] };

  const [totals, cityRows, skuRows] = await Promise.all([
    cubeLoad<Row>({
      measures: [
        `${REG}.my_gmv_8w`,
        `${REG}.cat_gmv_8w`,
        `${REG}.size_of_prize`,
        `${REG}.my_share`,
        `${REG}.count`,
      ],
      filters: [...productFilters(), provinceFilter],
      timeDimensions: periodFilter(period),
    }),
    cubeLoad<Row>({
      dimensions: [
        `${REG}.city_megastore_id`,
        `${CITY}.city`,
        `${CITY}.mp_score`,
      ],
      measures: [
        `${REG}.my_gmv_8w`,
        `${REG}.cat_gmv_8w`,
        `${REG}.size_of_prize`,
        `${REG}.my_share`,
      ],
      filters: [...productFilters(), provinceFilter],
      timeDimensions: periodFilter(period),
      order: { [`${REG}.size_of_prize`]: 'desc' },
      limit: 50,
    }),
    cubeLoad<Row>({
      dimensions: [
        `${PROD}.sku_id`,
        `${PROD}.description`,
        `${PROD}.brand`,
        `${REG}.gpiv_tier`,
      ],
      measures: [
        `${REG}.my_slope`,
        `${REG}.my_gmv_8w`,
        `${REG}.cat_gmv_8w`,
        `${REG}.size_of_prize`,
        `${REG}.my_share`,
      ],
      filters: [...productFilters(), provinceFilter],
      timeDimensions: periodFilter(period),
      order: { [`${REG}.size_of_prize`]: 'desc' },
      limit: 50,
    }),
  ]);

  const t = totals[0] ?? {};
  return {
    totals: {
      my_gmv:  Number(t[`${REG}.my_gmv_8w`]    ?? 0),
      cat_gmv: Number(t[`${REG}.cat_gmv_8w`]   ?? 0),
      sop:     Number(t[`${REG}.size_of_prize`] ?? 0),
      share:   Number(t[`${REG}.my_share`]     ?? 0),
      cities:  Number(t[`${REG}.count`]        ?? 0),
    },
    topCities: cityRows.map((r) => ({
      city_id: Number(r[`${REG}.city_megastore_id`]),
      city:    String(r[`${CITY}.city`] ?? '—'),
      mp:      Number(r[`${CITY}.mp_score`] ?? 0),
      my_gmv:  Number(r[`${REG}.my_gmv_8w`]    ?? 0),
      cat_gmv: Number(r[`${REG}.cat_gmv_8w`]   ?? 0),
      sop:     Number(r[`${REG}.size_of_prize`] ?? 0),
      share:   Number(r[`${REG}.my_share`]     ?? 0),
    })),
    topSkus: skuRows.map((r) => ({
      sku_id:    Number(r[`${PROD}.sku_id`]),
      sku:       String(r[`${PROD}.description`] ?? ''),
      brand:     String(r[`${PROD}.brand`] ?? ''),
      gpiv:      Number(r[`${REG}.my_slope`] ?? 0),
      gpiv_tier: String(r[`${REG}.gpiv_tier`] ?? 'flat'),
      my_gmv:    Number(r[`${REG}.my_gmv_8w`]  ?? 0),
      cat_gmv:   Number(r[`${REG}.cat_gmv_8w`] ?? 0),
      sop:       Number(r[`${REG}.size_of_prize`] ?? 0),
      share:     Number(r[`${REG}.my_share`]    ?? 0),
    })),
  };
}

export async function fetchCityDiagnostic(period: string, cityId: number): Promise<SkuInCity[]> {
  if (IS_OFFLINE) return offline.offline_fetchCityDiagnostic(cityId);
  const rows = await cubeLoad<Row>({
    dimensions: [
      `${PROD}.sku_id`,
      `${PROD}.description`,
      `${PROD}.brand`,
      `${REG}.gpiv_tier`,
    ],
    measures: [
      `${REG}.my_slope`,
      `${REG}.my_gmv_8w`,
      `${REG}.cat_gmv_8w`,
      `${REG}.size_of_prize`,
      `${REG}.my_share`,
    ],
    filters: [
      ...productFilters(),
      { member: `${REG}.city_megastore_id`, operator: 'equals', values: [String(cityId)] },
    ],
    timeDimensions: periodFilter(period),
    order: { [`${REG}.size_of_prize`]: 'desc' },
    limit: 500,
  });
  return rows.map((r) => ({
    sku_id:    Number(r[`${PROD}.sku_id`]),
    sku:       String(r[`${PROD}.description`] ?? ''),
    brand:     String(r[`${PROD}.brand`] ?? ''),
    gpiv:      Number(r[`${REG}.my_slope`] ?? 0),
    gpiv_tier: String(r[`${REG}.gpiv_tier`] ?? 'flat'),
    my_gmv:    Number(r[`${REG}.my_gmv_8w`]  ?? 0),
    cat_gmv:   Number(r[`${REG}.cat_gmv_8w`] ?? 0),
    sop:       Number(r[`${REG}.size_of_prize`] ?? 0),
    share:     Number(r[`${REG}.my_share`]    ?? 0),
  }));
}
